"use client";

import { imageApiUrl } from "@/lib/images/urls";

import Image from "next/image";
import Link from "next/link";
import { useMessages } from "next-intl";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Crown, Flame, Layers, Sparkles } from "lucide-react";

import type { SiteCopy, SiteLanguage } from "@/lib/site-data";
import { formatDate, formatDateTime } from "@/lib/site-date";
import type { NewsFeedMode, NewsFeedPage, StoryRecord } from "@/lib/site-queries";
import { BandBadge } from "@/components/site/band-badge";
import { usePreferences } from "@/components/site/preferences-provider";
import { withLanguagePrefix } from "@/lib/site-locale";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";

const PAGE_SIZE = 20;

const FEED_MODES: NewsFeedMode[] = ["new", "top", "hot"];

function pickTranslation(
  translations: Array<{ language: string; title: string; summary: string }>,
  language: SiteLanguage,
) {
  return translations.find((item) => item.language === language) ?? translations[0] ?? null;
}

function originLabel(originalLanguage: string | null, copy: SiteCopy): string {
  if (originalLanguage === "tr") return copy.ui.countryTurkey;
  if (originalLanguage === "de") return copy.ui.countryGermany;
  if (originalLanguage === "uk") return copy.ui.countryUkraine;
  return copy.ui.countryGlobal;
}

// Strip inline HTML snippets that occasionally leak through from
// feed descriptions. Intentionally minimal — runs on client only
// so we don't add a sanitizer dep for a best-effort display path.
function flattenText(value: string | null): string {
  if (!value) return "";
  return value.replace(/<[^>]*>/g, " ").replace(/&nbsp;/g, " ").replace(/&[a-zA-Z]+;/g, " ").trim();
}

/**
 * News feed — mix-match card rhythm.
 *
 * The old flat list of identical row cards was hypnotic (in the bad
 * way). This view cycles through three card shapes so the scroll has
 * varied rhythm and the reader's eye keeps engaging:
 *
 *   pattern = [std, std, std, DEEP, std, std, MULTI]   // length 7
 *
 * - std   → compact row card, small thumb left
 * - DEEP  → bigger hero card for stories with notable length/summary
 * - MULTI → outlet-grid card for stories with many sources
 *
 * The classifier is intentionally soft: at low source/summary counts
 * we still render the standard card. The point is visual rhythm, not
 * strict taxonomy.
 */
export function NewsView({ language }: { language: SiteLanguage }) {
  const { selectedSourceNames } = usePreferences();
  const [mode, setMode] = useState<NewsFeedMode>("new");
  const [rows, setRows] = useState<StoryRecord[]>([]);
  const [nextCursor, setNextCursor] = useState<string | null>(null);
  const [initialLoading, setInitialLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [failedImages, setFailedImages] = useState<Set<string>>(new Set());
  const copy = useMessages() as SiteCopy;
  const sentinelRef = useRef<HTMLDivElement | null>(null);
  const sourceKey = useMemo(() => selectedSourceNames.slice().sort().join("||"), [selectedSourceNames]);

  const fetchPage = useCallback(
    async (cursor: string | null, reset: boolean) => {
      if (reset) setInitialLoading(true);
      else setLoadingMore(true);
      setError(null);

      try {
        const params = new URLSearchParams({ mode, limit: String(PAGE_SIZE) });
        if (cursor) params.set("cursor", cursor);
        if (selectedSourceNames.length > 0) params.set("sources", selectedSourceNames.join(","));

        const response = await fetch(`/api/news/feed?${params.toString()}`, { cache: "no-store" });
        if (!response.ok) throw new Error(`Feed request failed (${response.status})`);

        const payload = (await response.json()) as NewsFeedPage;
        setRows((current) => (reset ? payload.items : [...current, ...payload.items]));
        setNextCursor(payload.nextCursor);
      } catch {
        setError(copy.ui.loadFeedError);
      } finally {
        if (reset) setInitialLoading(false);
        else setLoadingMore(false);
      }
    },
    [copy.ui.loadFeedError, mode, selectedSourceNames],
  );

  useEffect(() => {
    /* eslint-disable react-hooks/set-state-in-effect --
       Changing feed mode or source filters intentionally resets the
       client-side pagination cache before the fresh request lands. */
    setRows([]);
    setNextCursor(null);
    /* eslint-enable react-hooks/set-state-in-effect */
    void fetchPage(null, true);
  }, [fetchPage, mode, sourceKey]);

  useEffect(() => {
    const node = sentinelRef.current;
    if (!node || !nextCursor || initialLoading || loadingMore) return;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) {
          void fetchPage(nextCursor, false);
        }
      },
      { rootMargin: "800px 0px" },
    );
    observer.observe(node);
    return () => observer.disconnect();
  }, [fetchPage, nextCursor, initialLoading, loadingMore]);

  const markImageFailed = (id: string) =>
    setFailedImages((current) => {
      if (current.has(id)) return current;
      const next = new Set(current);
      next.add(id);
      return next;
    });

  // Pick the first row with a resolvable cover image for the top-wire
  // hero; an image-less top-wire reads as a dead zone and weakens the
  // whole feed. Fall back to row 0 only if *no* row has an image
  // (empty-state first load). The bypassed rows stay in `tail` so we
  // never drop content.
  const leadIndex = rows.findIndex((row) => row.hasImage);
  const lead = leadIndex >= 0 ? rows[leadIndex] : rows[0];
  const leadTranslation = lead ? pickTranslation(lead.translations, language) : null;
  const tail = useMemo(
    () => (lead ? rows.filter((row) => row.id !== lead.id) : []),
    [rows, lead],
  );

  // Classify each tail row into a variant for the mix-match rhythm.
  // See the pattern comment at the top of this file.
  const classified = useMemo(() => {
    return tail.map((item, index) => {
      const slot = index % 7; // 0..6
      // DEEP slot — favor rows that actually have a meaty summary;
      // otherwise fall back to standard so we don't show an empty
      // hero.
      if (slot === 3) {
        const summary = pickTranslation(item.translations, language)?.summary ?? item.ogDescription ?? item.shortContent;
        const looksDeep = Boolean(summary && summary.length > 140);
        return { item, variant: looksDeep ? "deep" : "standard" } as const;
      }
      // MULTI slot — needs at least 3 outlets to pay off visually.
      if (slot === 6 && item.sourceNames.length >= 3) {
        return { item, variant: "multi" } as const;
      }
      return { item, variant: "standard" } as const;
    });
  }, [tail, language]);

  return (
    <section className="space-y-8">
      <header className="flex flex-wrap items-end justify-between gap-4">
        <div className="space-y-1">
          <p className="text-xs uppercase tracking-[0.18em] text-rose-600 dark:text-rose-300">duudey / {copy.nav.news}</p>
          <h1 className="text-4xl font-black leading-tight text-foreground sm:text-5xl">
            {mode === "top" ? copy.ui.newsFeedTop : mode === "hot" ? copy.ui.newsFeedHot : copy.ui.newsFeedNew}
          </h1>
        </div>
        {/* Mode selector — Netflix-ish icons + brand-green accent for
            the active pill (distinct from the rose used elsewhere, so
            the user's current mode reads as a mode switch rather than
            "the hot thing"). Dark mode leans on the lighter emerald
            variant since the logo's green is darker than it. */}
        <div className="flex gap-1 rounded-full border border-border bg-muted p-1">
          {FEED_MODES.map((item) => {
            const Icon = item === "top" ? Crown : item === "hot" ? Flame : Sparkles;
            const label =
              item === "top" ? copy.ui.newsModeTop : item === "hot" ? copy.ui.newsModeHot : copy.ui.newsModeNew;
            const active = mode === item;
            return (
              <Button
                key={item}
                onClick={() => setMode(item)}
                variant="ghost"
                size="sm"
                className={cn(
                  "gap-1.5 rounded-full px-3 text-xs transition",
                  active
                    ? "bg-emerald-500/20 text-emerald-700 hover:bg-emerald-500/25 dark:bg-emerald-300/25 dark:text-emerald-100 dark:hover:bg-emerald-300/25"
                    : "text-foreground hover:bg-accent",
                )}
              >
                <Icon className={cn("size-3.5", active ? "text-emerald-600 dark:text-emerald-200" : "text-muted-foreground")} aria-hidden="true" />
                {label}
              </Button>
            );
          })}
        </div>
      </header>

      {error ? <p className="text-sm text-red-600 dark:text-red-300">{error}</p> : null}

      {!initialLoading && rows.length === 0 ? (
        <p className="text-sm text-muted-foreground">{copy.ui.noStoriesMatchFilters}</p>
      ) : null}

      {/* Lead (top wire) — always full-bleed hero style. */}
      {lead ? (
        <LeadCard
          story={lead}
          translation={leadTranslation}
          copy={copy}
          language={language}
          imageFailed={failedImages.has(lead.id)}
          onImageFail={() => markImageFailed(lead.id)}
        />
      ) : null}

      {/* Rhythm body: alternating card variants. */}
      <div className="grid gap-5">
        {classified.map(({ item, variant }) => {
          const translation = pickTranslation(item.translations, language);
          const imageFailed = failedImages.has(item.id);
          const onImageFail = () => markImageFailed(item.id);
          if (variant === "deep") {
            return (
              <DeepDiveCard
                key={item.id}
                story={item}
                translation={translation}
                copy={copy}
                language={language}
                imageFailed={imageFailed}
                onImageFail={onImageFail}
              />
            );
          }
          if (variant === "multi") {
            return (
              <MultiSourceCard
                key={item.id}
                story={item}
                translation={translation}
                copy={copy}
                language={language}
              />
            );
          }
          return (
            <StandardCard
              key={item.id}
              story={item}
              translation={translation}
              copy={copy}
              language={language}
              imageFailed={imageFailed}
              onImageFail={onImageFail}
            />
          );
        })}
      </div>

      {initialLoading ? <p className="text-sm text-muted-foreground">{copy.ui.loadingFeed}</p> : null}
      {!initialLoading && loadingMore ? <p className="text-sm text-muted-foreground">{copy.ui.loadingMore}</p> : null}
      <div ref={sentinelRef} className="h-8" />
    </section>
  );
}

// ---------------------------------------------------------------------
// Card variants
// ---------------------------------------------------------------------

type StoryCardProps = {
  story: StoryRecord;
  translation: { language: string; title: string; summary: string } | null;
  copy: SiteCopy;
  language: SiteLanguage;
  imageFailed?: boolean;
  onImageFail?: () => void;
};

/**
 * Full-bleed hero for the top-wire lead.
 *
 * Layout note — the desktop look is a single image tile with the
 * title/summary/source pills overlaid at the bottom. That pattern
 * collapses on phone widths: at ~400 px wide the 16/8 image is only
 * ~200 px tall, which is shorter than the overlay's natural height,
 * so the title clips at the top and the summary lands on top of the
 * photograph.
 *
 * Fix: on ``sm+`` we keep the overlay (content positioned absolutely
 * over the image). On mobile the same content node is taken out of
 * the absolute layer and rendered as a normal-flow dark panel
 * directly underneath the image. The image keeps a fixed aspect
 * ratio and the panel grows with the content, so nothing clips.
 */
function LeadCard({ story, translation, copy, language, imageFailed, onImageFail }: StoryCardProps) {
  const showImage = story.hasImage && !imageFailed;
  const leadArticle = story.articles[0];
  return (
    <article className="group relative overflow-hidden rounded-3xl border border-border bg-card/60">
      {/* Image tile. The gradient lives inside this container so the
          desktop overlay reads against a darkened lower-third. On
          mobile the gradient is harmless (it just darkens the
          bottom of the photo, which then meets the dark panel
          beneath it without a visible seam). */}
      <div className="relative aspect-[16/9] w-full overflow-hidden bg-muted sm:aspect-[16/8]">
        {showImage ? (
          <Image
            src={imageApiUrl("cluster", story.id)}
            alt={translation?.title ?? "Story"}
            fill
            priority
            sizes="(max-width: 768px) 100vw, 1100px"
            className="object-cover transition-transform duration-700 group-hover:scale-[1.02]"
            onError={onImageFail}
          />
        ) : (
          <div className="aurora-fallback absolute inset-0" aria-hidden="true" />
        )}
        <div className="absolute inset-0 bg-gradient-to-t from-zinc-950/90 via-zinc-950/35 to-transparent" />
      </div>
      {/* Title block.
          - Mobile: relative (in-flow) dark panel under the image.
          - sm+:    absolute, anchored to the bottom of the image
                    tile — the original overlay treatment. */}
      <div className="relative flex flex-col gap-3 bg-zinc-950 p-5 text-white sm:absolute sm:inset-x-0 sm:bottom-0 sm:bg-transparent sm:p-8">
        <span className="w-fit rounded-full bg-rose-500/95 px-2.5 py-0.5 text-[10px] font-mono font-semibold uppercase tracking-[0.16em] text-white">
          {copy.ui.topWire}
        </span>
        <Link
          href={withLanguagePrefix(`/clusters/${story.id}`, language)}
          className="text-2xl font-black leading-tight text-white hover:text-rose-200 sm:text-4xl"
        >
          {translation?.title ?? leadArticle?.title ?? copy.ui.untitledStory}
        </Link>
        <p className="max-w-3xl text-sm leading-relaxed text-white/80 sm:text-base">
          {translation?.summary ?? story.ogDescription ?? story.shortContent ?? copy.ui.noSummary}
        </p>
        <div className="flex flex-wrap items-center gap-1.5 pt-1">
          {story.articles.slice(0, 4).map((article) => (
            <a
              key={article.id}
              href={article.url}
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center rounded-full border border-white/25 bg-white/10 px-3 py-1 text-[11px] font-mono uppercase tracking-[0.12em] text-white backdrop-blur-md transition hover:border-rose-300/60 hover:bg-rose-500/30"
            >
              {article.sourceName}
            </a>
          ))}
        </div>
        <p className="text-[10px] font-mono uppercase tracking-[0.14em] text-white/70">
          {formatDateTime(story.updatedAt, language)} · {originLabel(story.originalLanguage, copy)}
        </p>
      </div>
    </article>
  );
}

/** Compact row card — thumb on the left, content right. The workhorse. */
function StandardCard({ story, translation, copy, language, imageFailed, onImageFail }: StoryCardProps) {
  const showImage = story.hasImage && !imageFailed;
  return (
    <article className="grid gap-4 rounded-2xl border border-border bg-card/40 p-4 transition hover:border-rose-400/40 sm:grid-cols-[160px_1fr] sm:p-5">
      <Link
        href={withLanguagePrefix(`/clusters/${story.id}`, language)}
        className="relative block aspect-[16/10] w-full overflow-hidden rounded-xl border border-border bg-muted sm:h-28 sm:w-40"
      >
        {showImage ? (
          <Image
            src={imageApiUrl("cluster", story.id)}
            alt={translation?.title ?? "Story"}
            fill
            sizes="160px"
            className="object-cover"

            onError={onImageFail}
          />
        ) : (
          <div className="aurora-fallback absolute inset-0" aria-hidden="true" />
        )}
      </Link>
      <div className="min-w-0 space-y-2">
        <div className="flex flex-wrap items-center gap-2 text-[10px] font-mono uppercase tracking-[0.14em] text-muted-foreground">
          <span>{formatDate(story.updatedAt, language)}</span>
          <span>·</span>
          <span>{originLabel(story.originalLanguage, copy)}</span>
        </div>
        <Link
          href={withLanguagePrefix(`/clusters/${story.id}`, language)}
          className="block text-base font-bold leading-snug text-foreground hover:text-rose-600 dark:hover:text-rose-300 sm:text-lg"
        >
          {translation?.title ?? story.articles[0]?.title ?? copy.ui.untitledStory}
        </Link>
        <p className="text-xs text-muted-foreground line-clamp-2">
          {flattenText(translation?.summary ?? story.ogDescription ?? story.shortContent ?? null) || copy.ui.noSummary}
        </p>
        <div className="flex flex-wrap items-center gap-1.5">
          {story.articles.slice(0, 3).map((article) => (
            <a
              key={article.id}
              href={article.url}
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center rounded-full border border-border bg-muted px-2.5 py-0.5 text-[10px] font-mono uppercase tracking-[0.12em] text-foreground transition hover:border-rose-300/60 hover:bg-rose-400/10"
            >
              {article.sourceName}
            </a>
          ))}
          {story.articles.length > 3 ? (
            <span className="inline-flex items-center rounded-full border border-border px-2.5 py-0.5 text-[10px] font-mono uppercase tracking-[0.12em] text-muted-foreground">
              +{story.articles.length - 3}
            </span>
          ) : null}
        </div>
        <div className="flex flex-wrap gap-1.5 pt-1">
          {story.bands.slice(0, 3).map((band) => (
            <BandBadge key={band.name} band={band} language={language} />
          ))}
        </div>
      </div>
    </article>
  );
}

/** Big hero card — long summary + outlet strip, Intercept-ish treatment. */
function DeepDiveCard({ story, translation, copy, language, imageFailed, onImageFail }: StoryCardProps) {
  const showImage = story.hasImage && !imageFailed;
  return (
    <article className="overflow-hidden rounded-3xl border border-border bg-card/60">
      <div className="grid gap-0 lg:grid-cols-[1.4fr_1fr]">
        <Link
          href={withLanguagePrefix(`/clusters/${story.id}`, language)}
          className="relative aspect-[16/10] w-full overflow-hidden bg-muted lg:aspect-auto lg:h-full"
        >
          {showImage ? (
            <Image
              src={imageApiUrl("cluster", story.id)}
              alt={translation?.title ?? "Story"}
              fill
              sizes="(max-width: 1024px) 100vw, 700px"
              className="object-cover"

              onError={onImageFail}
            />
          ) : (
            <div className="aurora-fallback absolute inset-0" aria-hidden="true" />
          )}
        </Link>
        <div className="flex flex-col gap-3 p-5 sm:p-7">
          <div className="flex items-center gap-2">
            <span className="inline-flex items-center gap-1 rounded-full bg-rose-500/95 px-2.5 py-0.5 text-[10px] font-mono font-semibold uppercase tracking-[0.16em] text-white">
              <Flame className="size-3" aria-hidden="true" />
              {copy.ui.deepCoverage}
            </span>
            <span className="text-[10px] font-mono uppercase tracking-[0.14em] text-muted-foreground">
              {formatDate(story.updatedAt, language)}
            </span>
          </div>
          <Link
            href={withLanguagePrefix(`/clusters/${story.id}`, language)}
            className="text-xl font-black leading-tight text-foreground hover:text-rose-600 dark:hover:text-rose-300 sm:text-2xl"
          >
            {translation?.title ?? story.articles[0]?.title ?? copy.ui.untitledStory}
          </Link>
          <p className="text-sm leading-relaxed text-muted-foreground">
            {flattenText(translation?.summary ?? story.ogDescription ?? story.shortContent ?? null) || copy.ui.noSummary}
          </p>
          <div className="flex flex-wrap items-center gap-1.5">
            {story.articles.slice(0, 4).map((article) => (
              <a
                key={article.id}
                href={article.url}
                target="_blank"
                rel="noreferrer"
                className="inline-flex items-center rounded-full border border-border bg-muted px-3 py-1 text-[11px] font-mono uppercase tracking-[0.12em] text-foreground transition hover:border-rose-300/60 hover:bg-rose-400/10"
              >
                {article.sourceName}
              </a>
            ))}
          </div>
          <div className="mt-auto flex flex-wrap gap-1.5 pt-1">
            {story.bands.slice(0, 4).map((band) => (
              <BandBadge key={band.name} band={band} language={language} />
            ))}
          </div>
        </div>
      </div>
    </article>
  );
}

/** Outlet-grid card — highlights multi-source coverage without an image. */
function MultiSourceCard({ story, translation, copy, language }: StoryCardProps) {
  return (
    <article className="rounded-2xl border border-rose-400/30 bg-gradient-to-br from-rose-500/8 via-card/50 to-card/50 p-5">
      <div className="flex flex-wrap items-center gap-2">
        <span className="inline-flex items-center gap-1 rounded-full bg-rose-500/95 px-2.5 py-0.5 text-[10px] font-mono font-semibold uppercase tracking-[0.16em] text-white">
          <Layers className="size-3" aria-hidden="true" />
          {copy.ui.outletsOnIt.replace("{count}", String(story.sourceNames.length))}
        </span>
        <span className="text-[10px] font-mono uppercase tracking-[0.14em] text-muted-foreground">
          {formatDate(story.updatedAt, language)} · {originLabel(story.originalLanguage, copy)}
        </span>
      </div>
      <Link
        href={withLanguagePrefix(`/clusters/${story.id}`, language)}
        className="mt-3 block text-xl font-black leading-tight text-foreground hover:text-rose-600 dark:hover:text-rose-300 sm:text-2xl"
      >
        {translation?.title ?? story.articles[0]?.title ?? copy.ui.untitledStory}
      </Link>
      {/* Outlet grid — 2 columns of outlet headlines so the card reads
          as a side-by-side perspective comparison, not a teaser. */}
      <div className="mt-4 grid gap-2 sm:grid-cols-2">
        {story.articles.slice(0, 4).map((article) => (
          <a
            key={article.id}
            href={article.url}
            target="_blank"
            rel="noreferrer"
            className="flex flex-col gap-0.5 rounded-xl border border-border bg-background/30 p-3 transition hover:border-rose-300/60 hover:bg-rose-400/5"
          >
            <span className="text-[10px] font-mono uppercase tracking-[0.16em] text-rose-600 dark:text-rose-300">
              {article.sourceName}
            </span>
            <span className="line-clamp-2 text-xs text-foreground">{article.title}</span>
          </a>
        ))}
      </div>
      <div className="mt-3 flex flex-wrap gap-1.5">
        {story.bands.slice(0, 4).map((band) => (
          <BandBadge key={band.name} band={band} language={language} />
        ))}
      </div>
    </article>
  );
}
